import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tab1PropertyDetails from "../../../components/public/userPostProperty/Tab1PropertyDetails";
import Tab2LocationDetails from "../../../components/public/userPostProperty/Tab2LocationDetails";
import Tab3MediaUploads from "../../../components/public/userPostProperty/Tab3MediaUploads";
import Tab4AmenitiesSection from "../../../components/public/userPostProperty/Tab4AmenitiesSection";
import { useAuth } from "../../../context/AuthContext";
import {
  AREA_UNITS,
  INITIAL_AREA,
  INITIAL_LOCATION,
} from "../../../utils/constants";
import { Check } from "lucide-react";
import { useFlowManager } from "../../../hooks/useFlowManager";

const INITIAL_FORM_DATA = {
  // Universal Fields
  propertyType: "residential",
  transactionType: "",
  description: "",
  whyToConsider: "",
  videoUrl: "",
  images: [],

  // Commercial Fields
  propertyName: "",
  suitableFor: "",
  propertyAge: "",
  expectedPrice: "",
  pricePerSqft: "",
  carpetArea: INITIAL_AREA,
  superArea: INITIAL_AREA,
  floor: "",
  availability: "",

  // Residential Fields
  brochureUrl: "",
  floorPlan: "",
  totalFloors: "",
  propertyFloor: "",
  projectName: "",
  builderName: "",
  brokerage: "1",
  ownership: "",
  amenities: [],

  // Land Fields
  isPreLeasedProperty: false,
  floorsAllowed: "",
  carpetAreaUnits: AREA_UNITS,
  noOfOpenSides: "",
  widthOfRoadFacing: {
    value: "",
    unit: "sqft",
  },
  roadWidthUnits: AREA_UNITS,

  // Location
  location: INITIAL_LOCATION,
};

const PostPropertyFormPage = () => {
  const DRAFT_KEY = "postPropertyDraft";
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(true);

  const navigate = useNavigate();
  const { user } = useAuth();
  const { goToChoosePlan } = useFlowManager();

  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_KEY);

    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);

        if (parsed.formData) setFormData(parsed.formData);
        if (typeof parsed.currentStep === "number")
          setCurrentStep(parsed.currentStep);

        console.log("Draft restored:", parsed);
      } catch (err) {
        console.error("Failed to restore draft:", err);
      }
    }

    setIsRestoring(false);
  }, []);

  useEffect(() => {
    if (isRestoring) return;

    const draft = {
      formData,
      currentStep,
    };

    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [formData, currentStep, isRestoring]);

  useState(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  useEffect(() => {
    if (!user) {
      navigate(`/post-property-login`, { state: { from: "/post-property" } });
    }
  }, [user, navigate]);

  // useEffect(() => {
  //   console.log("formdata:", formData);
  // }, [formData]);

  const nextStep = () =>
    setCurrentStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
  const prevStep = () => setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev));

  const baseSteps = [
    { number: 1, title: "Property details", subtitle: "Step 1" },
    { number: 2, title: "Location details", subtitle: "Step 2" },
    { number: 3, title: "Photos, Videos & Brochure", subtitle: "Step 3" },
  ];

  const fullSteps = [
    ...baseSteps,
    { number: 4, title: "Amenities & Final", subtitle: "Step 4" },
  ];

  const steps = formData.propertyType === "land" ? baseSteps : fullSteps;

  const preparePayload = (formData) => {
    if (!formData) return {};

    const payload = {
      type: formData.propertyType,
      description: formData.description || "",
      listingType: formData.transactionType || "",
      images: Array.isArray(formData.images) ? formData.images : [],
      videos: formData.videoUrl ? [formData.videoUrl] : [],
      location: formData.location || {},
    };

    switch (formData.propertyType) {
      case "residential":
        payload.title = formData.projectName;
        payload.property = {
          brochureUrl: formData.brochureUrl || "",
          whyToChooose: formData.whyToConsider || "",
          floors: {
            totalFloors: formData.totalFloors || "",
            propertyFloor: formData.propertyFloor || "",
          },
          floorPlan: formData.floorPlan,
          projectName: formData.projectName || "",
          builderName: formData.builderName || "",
          brokerage: formData.brokerage || "",
          ownership: formData.ownership || "",
          availability: formData.availability || "",
          amenities: Array.isArray(formData.amenities)
            ? formData.amenities
            : [],
        };
        break;

      case "commercial":
        payload.title = formData.propertyName;
        payload.property = {
          propertyAge: formData.propertyAge || null,
          suitableFor: formData.suitableFor || "",
          allowedForConstructionDetails: {
            expectedPrice: formData.expectedPrice || null,
            pricePerSqft: formData.pricePerSqft || null,
            carpetArea: formData.carpetArea || {},
            superArea: formData.superArea || {},
            floor: formData.propertyFloor || "",
            availability: formData.availability || "",
          },
          transactionType: formData.transactionType || "",
        };
        break;

      case "land":
        payload.title = formData.propertyName;
        payload.property = {
          isPreleasedProperty: formData.isPreLeasedProperty || false,
          floorsAllowedForConstruction: formData.floorsAllowed || null,
          carpetArea: formData.carpetArea || {},
          expectedPrice: formData.expectedPrice || null,
          pricePerSqft: formData.pricePerSqft || null,
          noOfOpenSides: formData.noOfOpenSides || null,
          widthOfRoadFacing: formData.widthOfRoadFacing || null,
        };
        break;

      default:
        payload.property = {};
    }

    return payload;
  };

  const timerRef = useRef(null);

  const handleCompleteForm = () => {
    setIsSubmitting(true);

    timerRef.current = setTimeout(() => {
      const payload = preparePayload(formData);
      goToChoosePlan(payload);
    }, 1700);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);


  const renderStep = () => {
    const type = formData.propertyType;

    switch (currentStep) {
      case 0:
        return (
          <Tab1PropertyDetails
            formData={formData}
            setFormData={setFormData}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 1:
        return (
          <Tab2LocationDetails
            formData={formData}
            setFormData={setFormData}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 2:
        return (
          <Tab3MediaUploads
            formData={formData}
            setFormData={setFormData}
            onNext={nextStep}
            onBack={prevStep}
            onSubmit={handleCompleteForm}
            isSubmitting={isSubmitting}
          />
        );
      case 3:
        return type !== "land" ? (
          <Tab4AmenitiesSection
            formData={formData}
            setFormData={setFormData}
            onNext={nextStep}
            onSubmit={handleCompleteForm}
            onBack={prevStep}
            isSubmitting={isSubmitting}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row  xl:h-[89vh] ">
      {/* MOBILE STEPPER */}
      <MobileStepper
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
      {/* LEFT PROGRESS COLUMN */}
      <div className="hidden lg:block lg:w-80 bg-white lg:min-h-full lg:sticky lg:top-0 shadow-sm">
        <div className="p-6 lg:p-8 space-y-2">
          {steps.map((step, idx) => (
            <ProgressStep
              key={idx}
              number={step.number}
              title={step.title}
              subtitle={step.subtitle}
              isActive={idx === currentStep}
              isCompleted={idx < currentStep}
              isLast={idx === steps.length - 1}
            />
          ))}
        </div>
      </div>

      {/* RIGHT SIDE CONTENT — scrollable */}
      <div className="flex-1 lg:flex overflow-y-auto">{renderStep()}</div>
    </div>
  );
};

export default PostPropertyFormPage;

const ProgressStep = ({
  number,
  title,
  subtitle,
  isActive,
  isCompleted,
  isLast,
}) => {
  return (
    <button
      className={`
        relative flex items-start gap-3 group w-full text-left transition-all
        ${isActive ? "cursor-default" : "cursor-auto"}
         rounded-lg p-2
      `}
    >
      {/* LEFT CIRCLE + LINE */}
      <div className="relative flex flex-col items-center pt-1">
        <div
          className={`
            w-10 h-10 flex items-center justify-center rounded-full transition-all
            ${
              isActive
                ? "bg-primary scale-110 shadow-md"
                : isCompleted
                ? "bg-primary shadow-sm"
                : "bg-gray-200 group-hover:bg-gray-300"
            }
          `}
        >
          <div
            className={`
              w-4 h-4 rotate-45 transition-all
              ${
                isActive || isCompleted
                  ? "bg-white"
                  : "bg-gray-400 group-hover:bg-gray-500"
              }
            `}
          />
        </div>

        {!isLast && (
          <div
            className={`
              w-0.5 h-16 mt-2 transition-all
              ${
                isCompleted
                  ? "bg-primary"
                  : "bg-gray-200 group-hover:bg-gray-300"
              }
            `}
          />
        )}
      </div>

      {/* STEP TEXT */}
      <div className="flex-1 pt-1">
        <h3
          className={`
            font-medium transition-all
            ${
              isActive
                ? "text-primary-navy text-base"
                : "text-text-secondary text-sm group-hover:text-primary"
            }
          `}
        >
          {title}
        </h3>
        <p className="text-xs text-text-muted mt-0.5">{subtitle}</p>
      </div>
    </button>
  );
};

const MobileStepper = ({ steps, currentStep }) => {
  return (
    <div className="lg:hidden w-full bg-white border-b px-4 py-3 sm:py-4">
      <div className="flex items-center justify-between max-w-md mx-auto">
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1;
          return (
            <div
              key={idx}
              className={`flex items-center ${isLast ? "" : "flex-1"}`}
            >
              <div className="flex flex-col items-center">
                <div
                  className={`
                  w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all
                  ${
                    idx === currentStep
                      ? "bg-primary text-white ring-4 ring-primary/20"
                      : idx < currentStep
                      ? "bg-green-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }
                `}
                >
                  {idx < currentStep ? (
                    <Check size={14} strokeWidth={3} />
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={`text-[0.59rem] sm:text-[0.6rem] mt-1.5 font-medium line-clamp-2 max-w-[70px] text-center
                  ${
                    idx === currentStep
                      ? "text-primary"
                      : idx < currentStep
                      ? "text-gray-700"
                      : "text-gray-400"
                  }
                `}
                >
                  {step.title}
                </span>
              </div>

              {!isLast && (
                <div
                  className={`
                  flex-1 h-0.5 mx-0.5 sm:mx-2 mb-4 sm:mb-8 transition-all
                  ${idx < currentStep ? "bg-green-600" : "bg-gray-200"}
                `}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
